/* What the reader is looking for, and how many things answered.
 *
 * A stream rather than a view setting, and that is the whole reason this file exists. View
 * settings are stashed into the user profile continuously, so a query stored there would survive
 * the session: a reader would open the view tomorrow with yesterday's filter still applied, most
 * of the estate dimmed, and nothing on screen saying why. A search is a question being asked now.
 *
 * The two directions do not meet. The field writes `query` and the view reads it; the view writes
 * `matchCount` and the field reads it. Nothing reads what it writes, so there is no loop to break.
 */

import {
  actionCreator,
  persistentReducedStream,
  reducer,
} from '@ardoq/rxbeach';

export type Network3DSearchState = {
  query: string;
  /** Rows that matched, not rows on screen: a group kept to place a result is not an answer. */
  matchCount: number;
};

export const network3DSearchChanged = actionCreator<string>(
  '[network3d] search changed'
);

export const network3DMatchCountChanged = actionCreator<number>(
  '[network3d] match count changed'
);

/** Dispatched when the view goes away, so the next one does not open mid-search. */
export const network3DSearchCleared = actionCreator(
  '[network3d] search cleared'
);

const defaultState: Network3DSearchState = { query: '', matchCount: 0 };

const setQuery = (
  state: Network3DSearchState,
  query: string
): Network3DSearchState => ({ ...state, query });

/* Held still when the number has not changed. The view recomputes this on every repack, and a
   fresh state object each time would re-render the settings bar at the frame rate. */
const setMatchCount = (
  state: Network3DSearchState,
  matchCount: number
): Network3DSearchState =>
  state.matchCount === matchCount ? state : { ...state, matchCount };

const clear = (): Network3DSearchState => defaultState;

export const network3dSearch$ = persistentReducedStream<Network3DSearchState>(
  'network3dSearch$',
  defaultState,
  [
    reducer(network3DSearchChanged, setQuery),
    reducer(network3DMatchCountChanged, setMatchCount),
    reducer(network3DSearchCleared, clear),
  ]
);
